import React, { Component } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import DropDown from "../Dropdown/Dropdown";
import styles from "./PhoneWidget.module.css";
import Input from "../Input/SimpleInput";
import { handleFormInput } from "../../shared/utilities/validations";
import { phoneNumberFormatValidation } from "../../shared/utilities/common";
import * as countryRepo from "../../shared/repos/graphql/country";
import * as phoneService from "../../shared/utilities/phone";
// REDUX
import * as countryActions from "../../redux/actions/country";
import * as checkoutActions from "../../redux/actions/checkout";

const defaultMask = "(###) ###-####";

class PhoneWidget extends Component {
  constructor(props) {
    super(props);

    this.state = {
      phoneCodes: [],
      selectOptions: [],
      phoneCode: "",
      phonePrefixId: "",
      phonePhone: "",
      telephone: "",
      currentPhoneMask: defaultMask
    };
  }

  getPreferredPhonePrefixId = () => {
    const { selectedCountry, isCountrySelectActive } = this.props;
    const preferredPrefixId = selectedCountry && selectedCountry.phone_prefix_id;
    if (isCountrySelectActive && preferredPrefixId) {
      return preferredPrefixId;
    }
    return null;
  };

  buildStateFromPrefix = prefix => {
    if (!prefix) {
      return null;
    }
    return {
      phonePrefixId: String(prefix.id),
      phoneCode: prefix.code,
      currentPhoneMask: phoneService.castMaskFormat(prefix.mask)
    };
  };

  resolveDropdownPrefixValue = () => {
    const { checkout } = this.props;
    const { phonePrefixId, phoneCodes, phoneCode } = this.state;
    if (checkout?.phonePrefixId) {
      return String(checkout.phonePrefixId);
    }
    if (phonePrefixId) {
      return String(phonePrefixId);
    }
    const dialCode = checkout?.phoneCode || phoneCode;
    if (dialCode && phoneCodes.length > 0) {
      const prefix = phoneService.findPhonePrefixByDialCode(
        dialCode,
        phoneCodes,
        this.getPreferredPhonePrefixId()
      );
      if (prefix) {
        return String(prefix.id);
      }
    }
    return dialCode || "";
  };

  componentDidMount = () => {
    this.getPhoneCodes().then(() => {
      const { code, phone, telephone } = this.props;
      const { phoneCodes } = this.state;
      if (code || phone) {
        this.setUpPhoneData(code, phone);
      } else if (telephone) {
        this.setupTelephone(telephone);
      } else if (phoneCodes.length > 0) {
        this.setupDefaultPhoneCode();
      }
    });
  };

  setUpPhoneData = (code, phone) => {
    const { phoneCodes } = this.state;
    const prefix = phoneService.findPhonePrefixByDialCode(
      code,
      phoneCodes,
      this.getPreferredPhonePrefixId()
    );
    const maskRegex = prefix
      ? phoneService.castMaskFormat(prefix.mask)
      : phoneService.castMaskFormat(phoneService.detectMask(code, phoneCodes));
    this.setState({
      phoneCode: code,
      phonePrefixId: prefix ? String(prefix.id) : "",
      phonePhone: phone,
      currentPhoneMask: maskRegex
    });
  };

  setupDefaultPhoneCode = () => {
    const { phoneCodes } = this.state;
    const { selectedCountry, isCountrySelectActive } = this.props;
    if (phoneCodes.length > 0) {
      let isPhoneCodeFound = false;
      const countryPrefixId = selectedCountry && selectedCountry.phone_prefix_id;
      const countryChanged = isCountrySelectActive && selectedCountry && countryPrefixId;
      if (countryChanged) {
        const phoneCode = _.find(phoneCodes, {
          id: countryPrefixId.toString()
        });
        if (phoneCode) {
          this.setState(this.buildStateFromPrefix(phoneCode));
          isPhoneCodeFound = true;
        }
      }
      if (isPhoneCodeFound === false) {
        this.setState(this.buildStateFromPrefix(phoneCodes[0]));
      }
    }
  };

  setupTelephone = (phone) => {
    const rawPhone = phoneService.detectPhone(phone);
    const code = phoneService.detectCountryCode(phone);
    const { phoneCodes } = this.state;
    let mask = defaultMask;
    let prefix = null;
    if (code) {
      prefix = phoneService.findPhonePrefixByDialCode(
        code,
        phoneCodes,
        this.getPreferredPhonePrefixId()
      );
      mask = prefix
        ? phoneService.castMaskFormat(prefix.mask)
        : phoneService.castMaskFormat(phoneService.detectMask(code, phoneCodes));
    }
    this.setState({
      telephone: phone,
      phoneCode: code,
      phonePrefixId: prefix ? String(prefix.id) : "",
      phonePhone: rawPhone,
      currentPhoneMask: mask
    });
  };

  getPhoneCodes = () => {
    const { setPhonePrefixes } = this.props;
    const promise = new Promise(resolve => {
      countryRepo.getPhonePrefixes().then(res => {
        setPhonePrefixes(res.data.phonePrefixes);
        this.updatePhonePrefixes(res.data.phonePrefixes, resolve);
      });
    });

    return promise;
  };

  updatePhonePrefixes = (prefixes, resolve) => {
    const options = _.map(prefixes, optionCode => {
      return {
        value: String(optionCode.id),
        label: `${this.getFlagEmoji(optionCode.country.flag)}  ${optionCode.code}`
      };
    });
    this.setState(
      {
        selectOptions: options,
        phoneCodes: prefixes
      },
      () => {
        const { phoneCodes } = this.state;
        resolve(phoneCodes);
      }
    );
  };

  getFlagEmoji = (countryCode) => {
    return countryCode.toUpperCase().replace(/./g, char =>
        String.fromCodePoint(127397 + char.charCodeAt())
    );
  }

  validateMask = (mask, phone) => {
    const maskLength = mask.split("#").length - 1;
    return phone.length === maskLength;
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { phonePhone, phoneCode, telephone, currentPhoneMask } = this.state;
    const { onInput, fieldName, code, phone } = this.props;

    const phoneNumberPropsChanged = prevProps.phone !== phone ||
      prevProps.code !== code
    if (phoneNumberPropsChanged) {
      this.setUpPhoneData(code, phone);
    }

    let phoneNumberChanged = prevState.phonePhone !== phonePhone ||
      prevState.phoneCode !== phoneCode

    if (phoneNumberChanged) {
      this.updatePhoneNumber(phoneCode, phonePhone);
    }

    phoneNumberChanged = prevState.telephone !== telephone
    if (phoneNumberChanged) {
      const phoneNumber = phoneService.formatPhoneNumber(phonePhone);
      onInput(fieldName, {
        telephone: phoneNumber && phoneNumber.trim() !== "" ? telephone : "",
        phone: phoneNumber,
        code: phoneCode,
        mask: currentPhoneMask,
        phoneFormat: phonePhone,
        maskValidation: this.validateMask(
          currentPhoneMask,
          phonePhone.replace(/\D/g, "")
        )
      });
    }
    this.onPropsTelephoneUpdate(prevProps.telephone);
  };

  onPropsTelephoneUpdate = prevPhone => {
    const { telephone } = this.props;
    if (prevPhone && prevPhone !== telephone) {
      const prefix = phoneService.detectCountryCode(telephone);
      const postfix = phoneService.detectPhone(telephone);
      if (prefix) {
        const { phoneCodes } = this.state;
        const prefixRow = phoneService.findPhonePrefixByDialCode(
          prefix,
          phoneCodes,
          this.getPreferredPhonePrefixId()
        );
        this.setState({
          phonePhone: postfix,
          phoneCode: prefix,
          phonePrefixId: prefixRow ? String(prefixRow.id) : ""
        });
      } else {
        this.setState({
          phonePhone: postfix
        });
      }
    }
  };

  updatePhoneNumber = (prefix, phone) => {
    this.setState({
      telephone: prefix
        ? `${prefix} ${phone.replace(/\D/g, "")}`
        : `${phone.replace(/\D/g, "")}`
    });
  };

  dropDownChange = e => {
    const { phoneCodes, telephone, phonePhone } = this.state;
    const { handleOnBlur, checkoutUpdated, checkout } = this.props;
    const prefix = phoneService.findPhonePrefixByOptionValue(e.value, phoneCodes);
    if (!prefix) {
      return;
    }
    const mask = phoneService.castMaskFormat(prefix.mask);

    checkout.phoneCode = prefix.code;
    checkout.phonePrefixId = String(prefix.id);
    checkoutUpdated(checkout);

    this.setState(this.buildStateFromPrefix(prefix), () => {
      const phone = phonePhone.replace(/\D/g, "");
      handleOnBlur({
        telephone: phone && phone.trim() !== "" ? telephone : "",
        phone,
        code: prefix.code,
        mask,
        phoneFormat: phonePhone,
        maskValidation: this.validateMask(mask, phonePhone.replace(/\D/g, ""))
      });
    });
  };

  handleOnBlur = () => {
    const { handleOnBlur } = this.props;
    const { telephone, phoneCode, phonePhone, currentPhoneMask } = this.state;
    const phone = phonePhone.replace(/\D/g, "");
    handleOnBlur({
      telephone: phone && phone.trim() !== "" ? telephone : "",
      phone,
      code: phoneCode,
      mask: currentPhoneMask,
      phoneFormat: phonePhone,
      maskValidation: this.validateMask(
        currentPhoneMask,
        phonePhone.replace(/\D/g, "")
      )
    });
  };

  PhoneWidgetContainer = () => {
    const {
      phonePhone,
      currentPhoneMask,
      selectOptions
    } = this.state;
    const {
      label,
      errorMessage,
      isEdit,
      isRequired,
      disabled,
      theme,
      fieldName
    } = this.props;
    const phoneWidgetErrorId = `phone-widget-error-${fieldName || 'phoneNumber'}`;
    return (
      <div
        className={`
      flex items-center justify-center gap-2 border-[1px] h-12
      ${theme === "dark" ? "border-darkElevationPrimary" : "border-borderLight dark:border-none !bg-white text-dark dark:text-white"} bg-transparent w-full rounded-[40px] h-11 px-2
    `}
      >
        <DropDown
          compactLayout
          ariaRequired={isRequired}
          onChange={e => this.dropDownChange(e)}
          options={selectOptions}
          value={this.resolveDropdownPrefixValue()}
          theme={!isEdit || theme === "dark" ? "Dark" : "Light"}
          customDropDownStyles=""
          className={`${theme === "light" ? styles.darkDropdown : styles.dropdown} phoneWidget`}
          disabled={disabled}
          ariaLabel="Country code"
          ariaDescribedBy={errorMessage ? phoneWidgetErrorId : undefined}
          ariaInvalid={Boolean(errorMessage)}
        />
        <Input
          customContainerStyles={theme === "dark" ? "!bg-transparent !outline-none !border-none h-11" : "!bg-white !outline-none !border-none h-11 rounded-[40px]"}
          handleInput={e => handleFormInput(e, this, "phonePhone")}
          label={
            typeof label === "string" && label.trim().length > 0
              ? label
              : "Phone number"
          }
          inputId={`${fieldName || "phone"}-local`}
          name={`${fieldName || "phone"}Local`}
          handleOnBlur={e => this.handleOnBlur(e)}
          placeholder={currentPhoneMask}
          theme={!isEdit ? "Dark" : "Light"}
          value={phonePhone}
          inputMask={currentPhoneMask}
          isNumberFormat
          isAllowed={phoneNumberFormatValidation}
          errorMessage={errorMessage}
          errorMessageId={phoneWidgetErrorId}
          isRequired={isRequired}
          fieldClass={`w-full bg-transparent outline-none h-11 ${theme === "dark" ? "text-white" : "text-dark !bg-white"}`}
          disabled={disabled}
        />
      </div>
    );
  };

  render = () => {
    return <this.PhoneWidgetContainer />;
  };
}
PhoneWidget.defaultProps = {
  telephone: "",
  fieldName: '',
  code: "",
  phone: "",
  label: "",
  errorMessage: "",
  isEdit: false,
  onInput: () => {},
  handleOnBlur: () => {},
  setPhonePrefixes: () => [],
  isRequired: false,
  isCountrySelectActive: false,
  disabled: false,
  theme: "dark"
};

PhoneWidget.propTypes = {
  telephone: PropTypes.string,
  fieldName: PropTypes.string,
  code: PropTypes.string,
  phone: PropTypes.string,
  label: PropTypes.string,
  errorMessage: PropTypes.string,
  onInput: PropTypes.func,
  handleOnBlur: PropTypes.func,
  isEdit: PropTypes.bool,
  setPhonePrefixes: PropTypes.func,
  isRequired: PropTypes.bool,
  disabled: PropTypes.bool,
  isCountrySelectActive: PropTypes.bool,
  checkout: PropTypes.shape().isRequired,
  checkoutUpdated: PropTypes.func.isRequired,
  selectedCountry: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    flag: PropTypes.string,
    path_url: PropTypes.string,
    api_url: PropTypes.string,
    short_name: PropTypes.string,
    url: PropTypes.string,
    zone_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    phone_prefix_id: PropTypes.number,
    currency: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      symbol: PropTypes.string
    })
  }).isRequired,
  theme: PropTypes.string
};

export const mapStateToProps = state => {
  const {
    phonePrefixes,
    selectedCountry,
    isCountrySelectActive
  } = state.country;
  return {
    phonePrefixes,
    selectedCountry,
    isCountrySelectActive,
    checkout: state.checkout
  };
};

export const mapDispatchToProps = dispatch => ({
  setPhonePrefixes: value => dispatch(countryActions.setPhonePrefixes(value)),
  checkoutUpdated: value => dispatch(checkoutActions.checkoutUpdated(value))
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PhoneWidget);
